/**
 * ConfirmModal — styled in-app confirmation dialog. Replaces native browser
 * `confirm()` calls so confirmations follow the app theme and feel consistent
 * across desktop + mobile (the native dialog renders different copy on each
 * browser/OS combo and shows the production hostname, which is jarring).
 *
 * Designed to mirror the reference screenshot: title at top, descriptive body
 * below, danger/primary action button at the right, secondary Cancel button.
 *
 * Usage:
 *   const [openConfirm, setOpenConfirm] = useState<{ id: number; title: string } | null>(null);
 *
 *   <ConfirmModal
 *     open={openConfirm !== null}
 *     title={openConfirm ? `Delete "${openConfirm.title}"?` : ""}
 *     body="This removes the item, its outline, and the generated article from the queue. This cannot be undone."
 *     confirmLabel="Delete"
 *     confirmVariant="danger"
 *     onConfirm={async () => {
 *       if (openConfirm) await deleteArticle(openConfirm.id);
 *       setOpenConfirm(null);
 *     }}
 *     onCancel={() => setOpenConfirm(null)}
 *   />
 */
import { useEffect, useRef } from "react";
import { Button } from "./Form";

interface ConfirmModalProps {
  open: boolean;
  title: string;
  body: string;
  confirmLabel?: string;
  cancelLabel?: string;
  confirmVariant?: "primary" | "danger";
  onConfirm: () => void | Promise<void>;
  onCancel: () => void;
}

export function ConfirmModal({
  open,
  title,
  body,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  confirmVariant = "primary",
  onConfirm,
  onCancel,
}: ConfirmModalProps) {
  const confirmBtnRef = useRef<HTMLButtonElement | null>(null);

  // Focus confirm button on open so Enter triggers it; ESC dismisses
  useEffect(() => {
    if (!open) return;
    confirmBtnRef.current?.focus();
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onCancel();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onCancel]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center px-4 z-50"
      onClick={onCancel}
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <div
        className="bg-bg-card border border-border rounded-lg shadow-2xl max-w-md w-full p-5 text-left"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-text font-semibold text-base mb-2">{title}</h3>
        <p className="text-text-muted text-sm leading-relaxed mb-5 whitespace-pre-line">
          {body}
        </p>
        <div className="flex items-center justify-end gap-2">
          <Button variant="secondary" size="sm" onClick={onCancel}>
            {cancelLabel}
          </Button>
          {/* Bound focus ref onto the confirm button via a wrapper so we can
              still use the design-system Button component without modifying it. */}
          <span
            ref={(node) => {
              confirmBtnRef.current = node?.querySelector("button") ?? null;
            }}
          >
            <Button
              variant={confirmVariant}
              size="sm"
              onClick={() => void onConfirm()}
            >
              {confirmLabel}
            </Button>
          </span>
        </div>
      </div>
    </div>
  );
}
